<template>
    <Head title="Collaboration Profile" />
    <BaseLayout gap="lg" content-class="text-valence-grey-800">
        <div class="flex flex-col gap-8">
            <div class="flex flex-col gap-3">
                <BasePageHeader :title="profileHeading" subtitle-below>
                    <template #subtitle>
                        <div v-if="is_self" class="flex flex-col items-start gap-2 md:flex-row md:items-baseline md:justify-between md:gap-4">
                            <span>Help others understand how to work with you best. You control who can view your profile.</span>
                            <BaseButton
                                theme="tertiary"
                                size="sm"
                                class="shrink-0 whitespace-nowrap px-0"
                                :class="{ 'animate-shadow-pulse': isFirstVisit }"
                                @click="isArcadeOpen = true">
                                How sharing works &#9656;
                            </BaseButton>
                        </div>
                        <template v-else>
                            This is the Collaboration Profile {{ displayName || "your colleague" }} has shared to help others work with them more effectively. Linking a profile to
                            a person can help you get more personalized coaching and insights from Nadia about how to collaborate with them.
                        </template>
                        <span v-if="profileUpdated" class="details" data-testid="profile-metadata">
                            <span v-if="is_self && accessText" data-testid="profile-access-text">Visible to {{ accessText }}&nbsp;&middot;&nbsp;</span>Last updated
                            {{ profileUpdated }}
                        </span>
                    </template>
                    <template v-if="isEditable" #actions>
                        <div class="flex gap-2">
                            <template v-if="currentAccessLevel !== ACCESS.PRIVATE">
                                <LoadingSpinner v-if="isGeneratingLink" class="self-start size-7 text-valence-grey-750" aria-label="Generating share link" />
                                <BaseTooltip v-else side="bottom" :side-offset="4">
                                    <template #trigger>
                                        <BaseButton
                                            theme="neutral"
                                            size="sm"
                                            class="self-start px-2 py-0 flex items-center justify-center size-7"
                                            :aria-label="isCopied ? 'Link copied' : 'Copy share link'"
                                            @click="handleCopyLinkWithToast">
                                            <i class="bi text-lg/none" :class="isCopied ? 'bi-check-lg text-green-600' : 'bi-link-45deg'" aria-hidden="true" />
                                        </BaseButton>
                                    </template>
                                    <template #content>Copy link</template>
                                </BaseTooltip>
                            </template>
                            <BaseButton
                                theme="secondary"
                                size="sm"
                                class="self-start flex items-center gap-2"
                                :class="{ 'animate-shadow-pulse': currentAccessLevel !== ACCESS.PRIVATE }"
                                @click="isShareProfileOpen = !isShareProfileOpen">
                                <i class="bi text-lg/none" :class="[currentAccessLevel === ACCESS.PRIVATE ? 'bi-eye-slash' : 'bi-eye']" aria-hidden="true"></i>
                                {{ shareButtonText }}
                            </BaseButton>
                        </div>
                    </template>
                </BasePageHeader>
                <StatusChip v-if="!is_self && isHrisInactive" state="neutral" size="xs" class="self-start">Inactive</StatusChip>
                <Transition name="fade">
                    <BaseButton
                        v-if="!is_self && connections_enabled && hasPublicProfileLinked && connectionId"
                        theme="primary"
                        class="self-start animate-shadow-pulse"
                        @click="navigateToConnection">
                        View Linked Person
                    </BaseButton>
                </Transition>
                <ShareProfileDialog
                    v-if="existing_public_profile?.id && isEditable"
                    v-model:open="isShareProfileOpen"
                    :existing-public-profile="existing_public_profile"
                    :org-has-ms-teams="org_has_ms_teams"
                    :org-has-slack="org_has_slack"
                    :available-access-levels="availableAccessLevels" />
            </div>
            <div class="rounded-2xl bg-valence-primary-800/10" data-testid="basic-info-card">
                <div class="flex flex-col gap-4">
                    <BasicInfoCard
                        :basic-info="basic_info"
                        :editable="isEditable"
                        :existing-public-profile="existing_public_profile"
                        :hris-data="hris_data ?? null"
                        :manager="org_tree?.manager ?? null"
                        :direct-reports="org_tree?.direct_reports ?? []"
                        :default-profile-access="default_profile_access"
                        @saved="handleBasicInfoSaved" />
                    <BaseNotification
                        v-if="isProfileUnderReview && is_self"
                        theme="info"
                        text="Your collaboration profile is under review. Once approved, others will be able to view it."
                        hide-dismiss />
                </div>
            </div>
            <div :id="LINKED_PROFILES_ANCHOR_ID">
                <div class="inline-flex max-md:flex-col items-baseline gap-x-4">
                    <h2 class="display-serif">How you work best</h2>
                    <p class="scaffold-body-small text-valence-grey-750">Based on Nadia's context. You can edit anything that doesn't sound like you.</p>
                </div>
                <HorizontalDivider class="border-t-2" />

                <div class="rounded-lgr border border-valence-grey-300 bg-white p-4 md:p-8 shadow-sm flex flex-col gap-4">
                    <BaseTabs v-model="activeProfileTab" :tab-ids="profileTabIds" hide-single-tab-list as-child class="gap-4" :unmount-on-hide="false">
                        <template #trigger-working>
                            <button type="button">
                                <span class="whitespace-nowrap">Working with me</span>
                            </button>
                        </template>
                        <template #trigger-team>
                            <button type="button">
                                <span class="whitespace-nowrap">How my team should work</span>
                            </button>
                        </template>
                        <template #content-working>
                            <ProfileTopicSection
                                v-if="is_self || workingWithMeEntries.length > 0 || isProfileTopicsPending"
                                :is-loading="isProfileTopicsPending"
                                :error="profileTopicsErrorMessage"
                                :has-profile="hasExistingPublicProfile"
                                :entries="workingWithMeEntries"
                                :empty-profile-state="workingEmptyProfileState"
                                :empty-entries-state="workingEmptyEntriesState"
                                :editable="isEditable"
                                :can-recreate="is_self"
                                :perspective-letters="perspectiveLetters"
                                :is-manager="isManager"
                                :has-perspective-report="hasPerspectiveReport"
                                :topic-name="PROFILE_TOPIC_NAMES.WORKING_WITH_ME"
                                :free-text-enabled="freeTextEnabled"
                                @saved="handleSubtopicSaved"
                                @recreate="handleProfileRegenerated">
                                <template #sidebar>
                                    <LinkedProfilesCard :linked-count="linked_profiles_count" @how-to-link="handleHowToLinkClick" />
                                </template>
                            </ProfileTopicSection>
                        </template>
                        <template #content-team>
                            <ProfileTopicSection
                                v-if="is_self || teamWorkEntries.length > 0 || isProfileTopicsPending"
                                :is-loading="isProfileTopicsPending"
                                :error="profileTopicsErrorMessage"
                                :has-profile="hasExistingPublicProfile"
                                :entries="teamWorkEntries"
                                :empty-profile-state="teamEmptyProfileState"
                                :empty-entries-state="teamEmptyEntriesState"
                                :perspective-letters="perspectiveLetters"
                                :editable="isEditable"
                                :can-recreate="is_self"
                                :is-manager="isManager"
                                :has-perspective-report="hasPerspectiveReport"
                                :topic-name="PROFILE_TOPIC_NAMES.TEAM_WORK"
                                :free-text-enabled="freeTextEnabled"
                                @saved="handleSubtopicSaved"
                                @recreate="handleProfileRegenerated">
                                <template #sidebar>
                                    <LinkedProfilesCard :linked-count="linked_profiles_count" @how-to-link="handleHowToLinkClick" />
                                </template>
                            </ProfileTopicSection>
                        </template>
                    </BaseTabs>
                </div>
            </div>
        </div>
    </BaseLayout>
    <BaseToast v-model:open="isCopyLinkSuccessToastOpen" :duration="TOAST_DURATION_MS" description="Profile link copied. Share it with a colleague." />
    <BaseToast v-model:open="isShareLinkErrorToastOpen" :duration="TOAST_DURATION_MS" theme="error" :description="shareLinkError ?? ''" />
    <BaseToast
        v-model:open="isAutoLinkSuccessToastOpen"
        :duration="TOAST_DURATION_MS"
        :description="autoLinkSuccessMessage"
        action-text="View Person"
        @action="handleViewAutoLinkedConnection" />
    <BaseToast
        v-model:open="isAutoLinkErrorToastOpen"
        :duration="TOAST_DURATION_MS"
        theme="error"
        description="We couldn't link this profile to your people"
        action-text="Try again"
        @action="autoLinkOnMount" />
    <DefaultSharingDialog v-model:open="isSharingIntroOpen" :access="currentAccessLevel" :available-access-levels="availableAccessLevels" />
    <DemoModal v-model:open="isArcadeOpen" :demo-url="SHARING_DEMO_URL" header-text="How sharing works" />
    <DemoModal v-model:open="isHowToLinkOpen" :demo-url="HOW_TO_LINK_DEMO_URL" header-text="How to link a colleague's profile" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { convertToDateTime } from "/js/dateUtils";
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { CollabProfileLinkResult } from "~vue/api/generated/collab-profiles";
import { dismissNotificationLocallyKey, type HrisData, type OrgTree, panesWithNotificationKey } from "~vue/appTypes";
import { useProfileShareLink } from "~vue/CollaborationProfile/composables/useProfileShareLink";
import DefaultSharingDialog from "~vue/CollaborationProfile/DefaultSharingDialog.vue";
import LinkedProfilesCard from "~vue/CollaborationProfile/LinkedProfilesCard.vue";
import ShareProfileDialog from "~vue/CollaborationProfile/ShareProfileDialog.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import BasicInfoCard from "~vue/components/BasicInfoCard.vue";
import DemoModal from "~vue/components/DemoModal.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { MainMenuPane } from "~vue/components/navigation/menuPanes";
import type { ProfileEntry } from "~vue/components/ProfileTopicSection.vue";
import ProfileTopicSection from "~vue/components/ProfileTopicSection.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { type ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { ACCESS, ACCESS_BUTTON_LABELS } from "~vue/utils/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { toProfileEntry } from "~vue/utils/profileEntries";
import { PROFILE_TOPIC_NAMES, type ProfileTopicName } from "~vue/utils/profileTopicNames";
import { DateTime } from "luxon";
import { computed, inject, nextTick, onMounted, reactive, ref, watch } from "vue";

const SHARING_DEMO_URL = "https://demo.arcade.software/NoUVnEW6MTDCEurJr1rm?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true";
const HOW_TO_LINK_DEMO_URL = "https://demo.arcade.software/jjw8Ow8XTFj1xizf5ePr?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true";

// Deep-link target for the coaching journey's "Link Collaboration Profiles" step, built in
// accounts.services.profile_completion_service.
const LINKED_PROFILES_ANCHOR_ID = "linked-profiles";
const LINKED_PROFILES_HASH = `#${LINKED_PROFILES_ANCHOR_ID}`;

const props = withDefaults(
    defineProps<{
        is_self?: boolean;
        user_name?: string | null;
        target_user_uuid?: string | null;
        basic_info: {
            full_name: string;
            role: string | null;
            team: string | null;
            level: string | null;
            organization: string | null;
        };
        available_access_levels?: string[];
        default_profile_access?: string;
        show_sharing_intro?: boolean;
        existing_public_profile?: ExistingPublicProfile | null;
        hris_data?: HrisData | null;
        org_tree?: OrgTree | null;
        org_has_ms_teams: boolean;
        org_has_slack: boolean;
        viewer_connection_id: string | null;
        is_collab_profile_linked: boolean;
        connections_enabled?: boolean;
        linked_profiles_count?: number;
        // this one encompasses both the assessment and report flags
        user_perspective_report_enabled: boolean;
    }>(),
    {
        is_self: true,
        user_name: null,
        target_user_uuid: null,
        available_access_levels: () => ["private", "organization"],
        show_sharing_intro: false,
        default_profile_access: ACCESS.ORGANIZATION,
        existing_public_profile: null,
        hris_data: null,
        org_tree: null,
        org_has_ms_teams: false,
        org_has_slack: false,
        is_collab_profile_linked: false,
        connections_enabled: false,
        linked_profiles_count: 0,
    },
);
const panesWithNotifications = inject(panesWithNotificationKey, ref([]));
const dismissNotificationLocally = inject(dismissNotificationLocallyKey, () => {});
watch(
    panesWithNotifications,
    async (newVal) => {
        if (newVal?.includes(MainMenuPane.COLLABORATION_PROFILE) && props.is_self) {
            dismissNotificationLocally(MainMenuPane.COLLABORATION_PROFILE);
            await useFetch("/v1/navigation/notifications/dismiss").post({ pane: "collaboration_profile" }).json();
        }
    },
    { immediate: true },
);

enum ProfileTabId {
    WorkingWithMe = "working",
    TeamWork = "team",
}

const activeProfileTab = ref<ProfileTabId>(ProfileTabId.WorkingWithMe);
const profileTabIds = computed(() => {
    const ids = [ProfileTabId.WorkingWithMe];
    if (isManager.value) {
        ids.push(ProfileTabId.TeamWork);
    }
    return ids;
});

interface SubtopicCurrent {
    content: string;
    access: string;
}

interface PropertySubtopic {
    id: string;
    title: string;
    placeholder: string;
    order: number;
    current?: SubtopicCurrent;
}

interface PropertyTopic {
    id: string;
    topic: string;
    placeholder: string;
    managers_only?: boolean;
    subtopics: PropertySubtopic[];
}

interface PropertyTopicsResponse {
    topics: PropertyTopic[];
}

const availableAccessLevels = computed(() => new Set(props.available_access_levels ?? []));
const hasExistingPublicProfile = computed(() => Boolean(props.existing_public_profile));
const isHrisInactive = computed(() => props.existing_public_profile?.is_inactive ?? false);
const isEditable = computed(() => hasExistingPublicProfile.value && props.is_self);
const isProfileUnderReview = computed(() => props.existing_public_profile?.is_flagged ?? false);
const perspectiveLetters = computed<string | null>(() => props.existing_public_profile?.perspective_letters ?? null);
const hasPerspectiveReport = computed(() => Boolean(perspectiveLetters.value));

const displayName = computed(() => props.user_name?.trim() || props.basic_info?.full_name || "");

const profileHeading = computed(() => {
    if (props.is_self) {
        return "Your Collaboration Profile";
    }
    if (displayName.value) {
        return `${displayName.value}'s Profile`;
    }
    return "Colleague Profile";
});

const profileTopicsUrl = computed(() => (props.is_self || !props.target_user_uuid ? "/api/accounts/profile/topics/" : `/api/accounts/profile/topics/${props.target_user_uuid}/`));

const {
    data: profileTopics,
    isFetching: isProfileTopicsLoading,
    error: profileTopicsError,
    execute: fetchProfileTopics,
} = useFetch(profileTopicsUrl, { immediate: false, refetch: true }).json<PropertyTopicsResponse>();

const profileTopicsErrorMessage = computed(() => {
    if (!profileTopicsError.value) {
        return null;
    }
    if (typeof profileTopicsError.value === "string") {
        return profileTopicsError.value;
    }
    return profileTopicsError.value.message || "Unable to load profile";
});

const shouldFetchProfileTopics = computed(() => hasExistingPublicProfile.value);

watch(
    shouldFetchProfileTopics,
    async (shouldFetch) => {
        if (shouldFetch) {
            await fetchProfileTopics();
        }
    },
    { immediate: true },
);

const hasProfileTopicsData = computed(() => profileTopics.value !== null);
const isProfileTopicsPending = computed(() => isProfileTopicsLoading.value && !hasProfileTopicsData.value);

const localSubtopicUpdates = reactive<Record<string, string>>({});

const freeTextEnabled = computed(() => !hasPerspectiveReport.value && !props.user_perspective_report_enabled);

const profileTopicMap = computed(() => {
    const topics = profileTopics.value?.topics ?? [];
    return new Map(topics.map((topic) => [topic.topic, topic]));
});

function buildProfileEntries(topicName: ProfileTopicName): ProfileEntry[] {
    const topic = profileTopicMap.value.get(topicName);
    if (!topic) {
        return [];
    }

    return topic.subtopics.map((subtopic) =>
        toProfileEntry({
            id: subtopic.id,
            title: subtopic.title,
            content: localSubtopicUpdates[subtopic.id] ?? subtopic.current?.content ?? "",
            placeholder: subtopic.placeholder,
        }),
    );
}

function handleBasicInfoSaved(): void {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_BASIC_INFO_SAVED);
    router.reload({ only: ["existing_public_profile"] });
    fetchProfileTopics();
}

function handleSubtopicSaved(id: string, content: string): void {
    void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_SUBTOPIC_EDITED, {
        subtopic_id: id,
        content_length: content.length,
    });
    localSubtopicUpdates[id] = content;
    fetchProfileTopics(); // background sync, no await
}

const workingWithMeEntries = computed(() => buildProfileEntries(PROFILE_TOPIC_NAMES.WORKING_WITH_ME));
const teamWorkEntries = computed(() => buildProfileEntries(PROFILE_TOPIC_NAMES.TEAM_WORK));

const PROFILE_MANAGE_HREF = "/profile/create";

const isManager = computed(() => props.existing_public_profile?.is_manager ?? false);

const workingEmptyProfileState = {
    title: "Add your collaboration profile",
    body: "Share how you work best so teammates can collaborate with you more effectively.",
    icon: "bi-person-lines-fill",
    ctaText: "Create your profile",
    ctaHref: PROFILE_MANAGE_HREF,
};

const workingEmptyEntriesState = {
    title: "No responses yet",
    body: "Add details about how you like to work with others.",
    icon: "bi-chat-left-text",
    ctaText: "Update your profile",
    // No ctaHref → renders as a button that emits @recreate (inline regenerate).
};

const teamEmptyProfileState = {
    title: "Add your collaboration profile",
    body: "Share how you want your team to work so expectations are clear.",
    icon: "bi-people",
    ctaText: "Create your profile",
    ctaHref: PROFILE_MANAGE_HREF,
};

const teamEmptyEntriesState = {
    title: "No responses yet",
    body: "Add details about how you want your team to collaborate.",
    icon: "bi-kanban",
    ctaText: "Generate with Nadia",
    // No ctaHref → renders as a button that emits @recreate (inline regenerate).
};

const isFirstVisit = computed(() => panesWithNotifications.value?.includes(MainMenuPane.COLLABORATION_PROFILE));
const isArcadeOpen = ref(false);
const isHowToLinkOpen = ref(false);
const isShareProfileOpen = ref<boolean>(false);

const handleHowToLinkClick = () => {
    isHowToLinkOpen.value = true;
    void logUserInteraction(AmplitudeEventNames.PROFILE_LINK_SUGGESTION_EXPLAINER_CLICKED, { surface: "collaboration_profile" });
};

const connectionId = computed(() => props.viewer_connection_id ?? null);
const hasPublicProfileLinked = computed(() => props.is_collab_profile_linked);

const navigateToConnection = () => {
    router.visit(`/people/${connectionId.value}/`);
};

const handleProfileRegenerated = async (): Promise<void> => {
    Object.keys(localSubtopicUpdates).forEach((id) => delete localSubtopicUpdates[id]);
    await fetchProfileTopics();
    router.reload({ only: ["existing_public_profile"] });
};

const currentAccessLevel = computed<ACCESS>(() => (props.existing_public_profile?.access as ACCESS | undefined) ?? ACCESS.PRIVATE);

const isSharingIntroOpen = ref(props.show_sharing_intro);

const shareButtonText = computed(() => ACCESS_BUTTON_LABELS[currentAccessLevel.value]);

const accessText = computed(() => {
    switch (currentAccessLevel.value) {
        case ACCESS.PRIVATE:
            return "you only";
        case ACCESS.ORGANIZATION:
            return "your organization";
        case ACCESS.INVITED_ORG_MEMBERS:
            return "your invited members";
        default:
            return "";
    }
});

const profileUpdated = computed(() => {
    if (props.existing_public_profile?.updated_at) {
        const date = convertToDateTime(props.existing_public_profile.updated_at);
        return date.toLocaleString(DateTime.DATETIME_SHORT);
    }
    return null;
});

const { isCopied, handleCopyLink, isGeneratingLink, shareLinkError } = useProfileShareLink({
    profileId: computed(() => props.existing_public_profile?.id),
});

const isCopyLinkSuccessToastOpen = ref(false);
const handleCopyLinkWithToast = async () => {
    await handleCopyLink(currentAccessLevel.value, () => {
        isCopyLinkSuccessToastOpen.value = true;
    });
};

const TOAST_DURATION_MS = 6000;

const isShareLinkErrorToastOpen = ref(false);
watch(shareLinkError, (err) => {
    if (err) {
        isShareLinkErrorToastOpen.value = true;
    }
});

const isAutoLinkSuccessToastOpen = ref<boolean>(false);
const autoLinkSuccessMessage = ref<string>("");

const ensureLinkUrl = computed(() => (props.target_user_uuid ? `/v1/collaboration-profiles/${props.target_user_uuid}/ensure-link` : ""));
const { data: ensureLinkData, error: ensureLinkError, execute: executeEnsureLink } = useFetch(ensureLinkUrl, { immediate: false }).post().json<CollabProfileLinkResult>();

const handleViewAutoLinkedConnection = (): void => {
    const id = ensureLinkData.value?.connection_id;
    if (id) {
        router.visit(`/people/${id}/`);
    }
};

const isAutoLinkErrorToastOpen = ref<boolean>(false);

const autoLinkOnMount = async (): Promise<void> => {
    if (!props.connections_enabled || props.is_self || !props.target_user_uuid || (props.is_collab_profile_linked && props.viewer_connection_id)) {
        return;
    }

    await executeEnsureLink();
    if (ensureLinkError.value) {
        isAutoLinkErrorToastOpen.value = true;
        return;
    }
    const result = ensureLinkData.value;
    if (!result || result.action === "noop") {
        return;
    }
    autoLinkSuccessMessage.value =
        result.action === "created"
            ? `Added ${props.basic_info.full_name} to your people and linked collaboration profile`
            : `Linked ${props.basic_info.full_name} to an existing person`;
    await new Promise<void>((resolve) =>
        router.reload({
            only: ["is_collab_profile_linked", "viewer_connection_id"],
            onFinish: () => resolve(),
        }),
    );
    isAutoLinkSuccessToastOpen.value = true;
};

onMounted(() => {
    void autoLinkOnMount();
    if (props.show_sharing_intro) {
        // Inertia restores cached props on back/forward, so drop the flag from history state too.
        router.replace({
            props: (currentProps) => ({ ...currentProps, show_sharing_intro: false }),
            preserveState: true,
            preserveScroll: true,
        });
    }
});

if (props.is_self && globalThis.location.hash === LINKED_PROFILES_HASH) {
    watch(
        isProfileTopicsPending,
        async () => {
            await nextTick();
            document.getElementById(LINKED_PROFILES_ANCHOR_ID)?.scrollIntoView({ behavior: "smooth" });
        },
        { once: true },
    );
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
